import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { DocumentSummary } from "../api/types";
import { Icon } from "../components/Icon";
import { MAX_COMPARED } from "./Compare";
import { trackEvent } from "../telemetry";

type SortOrder = "newest" | "oldest" | "name" | "project";

// Project names and filenames are Polish, so Ł and Ż sort where a Polish
// reader expects them.
const byText = (a: string, b: string) => a.localeCompare(b, "pl", { sensitivity: "base" });

function sortDocuments(docs: DocumentSummary[], order: SortOrder): DocumentSummary[] {
  const newest = (a: DocumentSummary, b: DocumentSummary) => b.created_at.localeCompare(a.created_at);
  return [...docs].sort((a, b) => {
    if (order === "oldest") return -newest(a, b);
    if (order === "name") return byText(a.original_filename, b.original_filename);
    if (order === "project") {
      // Documents without a project go last, not first.
      if (!a.project_name !== !b.project_name) return a.project_name ? -1 : 1;
      return byText(a.project_name ?? "", b.project_name ?? "") || newest(a, b);
    }
    return newest(a, b);
  });
}

export function DocumentsList() {
  const [order, setOrder] = useState<SortOrder>("newest");
  const [filter, setFilter] = useState("");
  const [compared, setCompared] = useState<string[]>([]);
  const navigate = useNavigate();
  // ponytail: sorts and filters on the client, over the newest 100 packages the
  // API returns. Move both into the query when the list outgrows that.
  const docsQuery = useQuery({
    queryKey: ["documents"],
    queryFn: api.listDocuments,
  });

  const docs = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase("pl");
    const matching = (docsQuery.data ?? []).filter(
      (d) =>
        !needle ||
        [d.original_filename, d.project_name ?? "", ...d.package_filenames].some((value) =>
          value.toLocaleLowerCase("pl").includes(needle),
        ),
    );
    return sortDocuments(matching, order);
  }, [docsQuery.data, filter, order]);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600 }}>
            DOCUMENTS
          </div>
          <h2 style={{ fontSize: 22, margin: "4px 0 0 0" }}>Recent analyses</h2>
        </div>
        <Link
          to="/upload"
          className="btn btn-primary"
          onClick={() => trackEvent("new_analysis_selected")}
        >
          New analysis
        </Link>
      </div>

      {(docsQuery.data?.length ?? 0) > 0 && (
        <div className="list-controls">
          <input
            className="field"
            type="search"
            placeholder="Filter by project or file name"
            aria-label="Filter by project or file name"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          <label className="list-sort">
            <span>Sort by</span>
            <select
              className="field"
              value={order}
              onChange={(event) => setOrder(event.target.value as SortOrder)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">File name A–Z</option>
              <option value="project">Project</option>
            </select>
          </label>
        </div>
      )}

      {compared.length > 0 && (
        <div className="compare-bar" role="status">
          <span>
            Selected for comparison: {compared.length} of {MAX_COMPARED}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setCompared([])}>
            Clear
          </button>
          <button
            className="btn btn-primary btn-sm"
            disabled={compared.length < 2}
            onClick={() => {
              trackEvent("comparison_opened", { document_count: compared.length });
              navigate(`/compare?docs=${compared.join(",")}`);
            }}
          >
            Compare side by side
          </button>
        </div>
      )}

      {docsQuery.isLoading && <div>Loading…</div>}
      {docsQuery.data?.length === 0 && (
        <div className="card card-pad" style={{ color: "var(--ink-3)" }}>
          No documents yet. Upload one to get started.
        </div>
      )}
      {docsQuery.data && docsQuery.data.length > 0 && docs.length === 0 && (
        <div className="card card-pad" style={{ color: "var(--ink-3)" }}>
          No analyses match the filter.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {docs.map((d, index) => (
          <div key={d.id} style={{ display: "contents" }}>
            {order === "project" && d.project_name !== docs[index - 1]?.project_name && (
              <h3 className="list-group-head">{d.project_name ?? "No project"}</h3>
            )}
            <div
              className="card card-pad"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
              }}
            >
              <Link
                to={`/docs/${d.id}/wizard`}
                onClick={() =>
                  trackEvent("document_opened", {
                    document_id: d.id,
                    status: d.status,
                    page_count: d.page_count,
                    package_document_count: d.package_document_count,
                  })
                }
                style={{ textDecoration: "none", color: "inherit", flex: 1, minWidth: 0 }}
              >
                <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{d.original_filename}</div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                  {d.project_name && order !== "project" && (
                    <span className="project-chip" style={{ marginRight: 8 }}>
                      <Icon name="folder" size={11} /> {d.project_name}
                    </span>
                  )}
                  {d.page_count ?? "?"} pages · status: {d.status}
                </div>
              </Link>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                {d.status === "ocr_done" && (
                  <label className="compare-pick" title="Select for side-by-side comparison">
                    <input
                      type="checkbox"
                      checked={compared.includes(d.id)}
                      disabled={!compared.includes(d.id) && compared.length >= MAX_COMPARED}
                      onChange={(event) =>
                        setCompared((current) =>
                          event.target.checked
                            ? [...current, d.id]
                            : current.filter((id) => id !== d.id),
                        )
                      }
                    />
                    Compare
                  </label>
                )}
                {d.status === "ocr_done" && (
                  <Link
                    to={`/docs/${d.id}/package`}
                    className="btn btn-secondary"
                    onClick={() => trackEvent("package_edit_selected", { document_id: d.id })}
                  >
                    Edit metadata
                  </Link>
                )}
                <div style={{ fontSize: 11, color: "var(--ink-4)", whiteSpace: "nowrap" }}>
                  {new Date(d.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
